// store global types
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types
import type {
    TApiODIN,
    TErrorLoginCallback,
    TResponseLoginCallback,
} from "@/apis/odin/apiODIN";

type TStateLogin = {
    loginCallbackState: TAsyncState<
        TResponseLoginCallback,
        TErrorLoginCallback
    >;
};

type TActionsPublicLogin = {
    resetStateLogin: () => void;
    onLoginCallback: ({ returnTo }: { returnTo?: string }) => Promise<void>;
};

type TActionsPrivateLogin = {
    setLoginCallbackPending: () => void;
    setLoginCallbackSuccess: (data: TResponseLoginCallback) => void;
    setLoginCallbackError: (error: TErrorLoginCallback) => void;
};

type TSliceDependenciesLogin = {
    apiODIN: TApiODIN;
    navigateToReferer: ({ url }: { url: string | null }) => void;
};

export type TSliceLogin = TStateLogin & TActionsPublicLogin;

export const initialStateLogin = {
    loginCallbackState: {
        status: "idle",
        data: null,
        error: null,
    },
} as const satisfies TStateLogin;

export const createSliceLogin: TCreateSlice<
    TSliceLogin,
    TSliceDependenciesLogin
> =
    ({ apiODIN, navigateToReferer }) =>
    set => {
        const actionsPrivate: TActionsPrivateLogin = {
            setLoginCallbackPending: () => {
                set(
                    {
                        loginCallbackState: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "login/setLoginCallbackPending",
                );
            },
            setLoginCallbackSuccess: (data: TResponseLoginCallback) => {
                set(
                    {
                        loginCallbackState: {
                            status: "resolved",
                            data,
                            error: null,
                        },
                    },
                    false,
                    "login/setLoginCallbackSuccess",
                );
            },
            setLoginCallbackError: (error: TErrorLoginCallback) => {
                set(
                    {
                        loginCallbackState: {
                            status: "rejected",
                            data: null,
                            error,
                        },
                    },
                    false,
                    "login/setLoginCallbackError",
                );
            },
        };

        const actionsPublic: TActionsPublicLogin = {
            resetStateLogin: () => {
                set(initialStateLogin, false, "login/resetState");
            },
            onLoginCallback: async ({ returnTo }: { returnTo?: string }) => {
                actionsPrivate.setLoginCallbackPending();
                try {
                    const response = await apiODIN.userLoginCallback();
                    actionsPrivate.setLoginCallbackSuccess(response.data);
                    navigateToReferer({ url: returnTo ?? "/" });
                } catch (error) {
                    actionsPrivate.setLoginCallbackError(error);
                }
            },
        };

        return {
            ...initialStateLogin,
            ...actionsPublic,
        };
    };
